// PROCORE / ACC FOLDERS (owner 2026-10-25: "each project/company/per procore and per acc
// accounts to have upload path for models, rvt, nwc, nwd and download path for rvt, nwc,
// nwd and upload path for shopdrawing and download path for shop drawings").
//
// Every Procore and ACC account the company has connected, each with its four slots:
//   Models - download     default folder, and optional .rvt / .nwc / .nwd folders
//   Models - upload       the same
//   Shop drawings - download
//   Shop drawings - upload
// scope "project": the project's own folders per account (a slot it does not set shows the
// company default it uses); scope "company": the company defaults every project falls back on.
// Each folder is picked with FolderPicker and shown as its full path text.
import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Modal, Spin } from "antd";
import addNotification from "helpers/notify";
import FolderPicker from "./FolderPicker";
import { AutodeskLogo, ProcoreLogo } from "./ProviderLogos";
import { getCompanyFolderPaths, saveCompanyFolderPaths, getProjectFolderPaths, saveProjectFolderPaths } from "./folderPathsApi";
import { refreshIntegrationCards } from "./useIntegrations";
import "./integrations.scss";

const NAME = { acc: "ACC", procore: "Procore" };
const LEVELS = [
  { level: "base", label: "Default (all types)" },
  { level: "rvt", label: ".rvt" },
  { level: "nwc", label: ".nwc" },
  { level: "nwd", label: ".nwd" },
];
const FROM = { project: "this project", company: "company default" };

// a folder target's project, for opening the picker inside it
const startFrom = (t) => {
  if (!t || !t.accountId || !t.containerId) return null;
  const [accountName, containerName] = String(t.path || "").split(" / ");
  return { accountId: t.accountId, containerId: t.containerId, accountName, containerName };
};

/**
 * One row: what the slot uses and why. `own` is the folder set at THIS level (project or
 * company), `used` the one in effect (project scope: resolved with the company's).
 */
function SlotRow({ label, own, used, inherit, canEdit, saving, onPick, onClear, testId }) {
  let text = <span className="text-muted">Not set</span>;
  let tag = "";
  if (own) {
    text = <span className="integration-path">{own.path}</span>;
  } else if (used) {
    text = <span className="integration-path text-muted">{used.path}</span>;
    tag = inherit;
  }
  return (
    <div className="integration-row folder-paths-row" data-testid={testId}>
      <b className="folder-paths-label">{label}</b>
      <span className="folder-paths-value">
        {text}
        {tag ? <small className="text-muted"> ({tag})</small> : null}
      </span>
      {canEdit ? (
        <span className="folder-paths-actions">
          <button className="integration-link-btn" disabled={saving} onClick={onPick}>
            {own ? "Change" : "Choose folder"}
          </button>
          {own ? (
            <button className="integration-link-btn danger" disabled={saving} onClick={onClear}>
              Clear
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

/** The four slots of one account (and, on a project, one of its destinations). */
function SlotsBlock({ slots, scope, own, effective, canEdit, saving, onPick, onClear, keyPrefix }) {
  return (
    <div className="folder-paths-slots">
      {slots.map((s) => (
        <div key={s.slot} className="integration-kind">
          <div className="integration-kind-head">
            <b>{s.label}</b>
          </div>
          <small className="text-muted d-block mb-1">{s.hint}</small>
          {s.types ? (
            LEVELS.map(({ level, label }) => {
              const mine = own[s.slot] && own[s.slot][level];
              let used = null;
              let inherit = "";
              if (scope === "project") {
                const r = effective && effective[s.slot] && effective[s.slot][level];
                used = r ? r.target : null;
                inherit = r ? (r.from === "company" ? FROM.company : level !== "base" ? "uses the default folder" : FROM.project) : "";
              } else if (level !== "base" && own[s.slot] && own[s.slot].base) {
                used = own[s.slot].base;
                inherit = "uses the default folder";
              }
              return (
                <SlotRow
                  key={level}
                  label={label}
                  own={mine}
                  used={used}
                  inherit={inherit}
                  canEdit={canEdit}
                  saving={saving}
                  onPick={() => onPick(s, level, mine || used)}
                  onClear={() => onClear(s, level)}
                  testId={`${keyPrefix}-${s.slot}-${level}`}
                />
              );
            })
          ) : (
            <SlotRow
              label="Folder"
              own={own[s.slot]}
              used={scope === "project" && effective && effective[s.slot] ? effective[s.slot].target : null}
              inherit={scope === "project" && effective && effective[s.slot] ? FROM[effective[s.slot].from] : ""}
              canEdit={canEdit}
              saving={saving}
              onPick={() => onPick(s, "", own[s.slot] || (effective && effective[s.slot] && effective[s.slot].target))}
              onClear={() => onClear(s, "")}
              testId={`${keyPrefix}-${s.slot}`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

export default function FolderPathsPanel({ scope = "project", projectId, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [picking, setPicking] = useState(null); // { account, dest, slot, level, start }

  const load = useCallback(async () => {
    try {
      const d = scope === "company" ? await getCompanyFolderPaths() : await getProjectFolderPaths(projectId);
      setData(d);
      setError("");
    } catch (e) {
      setError(e.message);
    }
  }, [scope, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const canEditFor = (provider) => (scope === "company" ? !!(data && data.canEdit) : !!(data && data.canEdit && data.canEdit[provider]));

  const save = async (account, dest, slot, level, target) => {
    const paths = { [slot.slot]: slot.types ? { [level]: target } : target };
    setSaving(true);
    try {
      if (scope === "company") await saveCompanyFolderPaths(account.provider, { connectionId: account.connection, paths });
      else await saveProjectFolderPaths(projectId, account.provider, { ...(dest ? { destination: dest.destination } : { connectionId: account.connection }), paths });
      const what = `${slot.label}${slot.types && level !== "base" ? ` .${level}` : ""}`;
      addNotification(target ? `${NAME[account.provider]} ${what}: ${target.path}` : `${NAME[account.provider]} ${what} cleared`, "success");
      setPicking(null);
      await load();
      if (scope === "project") refreshIntegrationCards();
      if (onChanged) onChanged();
    } catch (e) {
      addNotification(e.message, "danger");
    } finally {
      setSaving(false);
    }
  };

  if (error) return <p className="text-danger">{error}</p>;
  if (!data) return <Spin />;
  const slots = data.slots || [];

  if (picking) {
    const { account, dest, slot, level, start } = picking;
    return (
      <div data-testid="folder-paths-picking">
        <p className="mb-2">
          <b>
            {NAME[account.provider]} - {account.label}: {slot.label}
            {slot.types ? ` - ${LEVELS.find((l) => l.level === level).label}` : ""}
          </b>
          <br />
          <small className="text-muted">Open the folder, then Use this folder.</small>
        </p>
        <FolderPicker
          provider={account.provider}
          connection={account.connection}
          start={startFrom(start)}
          busy={saving}
          onCancel={() => setPicking(null)}
          onPick={(t) => save(account, dest, slot, level, { accountId: t.accountId, containerId: t.containerId, folderId: t.folderId, path: t.path })}
        />
      </div>
    );
  }

  if (!data.accounts || !data.accounts.length) {
    return (
      <p className="text-muted" data-testid="folder-paths-no-accounts">
        No Procore or ACC account is connected for this company yet. Connect one under My Profile (Procore / ACC tabs), then choose its folders here.
      </p>
    );
  }

  return (
    <div className="folder-paths-panel" data-testid={`folder-paths-${scope}`}>
      <p className="text-muted">
        {scope === "company"
          ? "Default folders for every project of the company. A project that sets a folder of its own uses that one instead."
          : "This project's folders per account. A slot left empty uses the company default (Profile > Procore / ACC folders), and a type left empty uses the default folder."}
      </p>
      {data.accounts.map((a) => {
        const Logo = a.provider === "acc" ? AutodeskLogo : ProcoreLogo;
        const keyPrefix = `fp-${a.provider}-${a.connection || "primary"}`;
        const canEdit = canEditFor(a.provider);
        const head = (
          <div className="integration-section-title d-flex align-items-center" style={{ gap: 6 }}>
            <Logo connected={!!a.connected} size={18} />
            {NAME[a.provider]} - {a.label}
            {a.primary ? " (company account)" : ""}
            {!a.connected ? <span className="text-danger"> - not connected</span> : null}
          </div>
        );
        if (scope === "company") {
          return (
            <div key={keyPrefix} className="integration-section" data-testid={keyPrefix}>
              {head}
              <SlotsBlock
                slots={slots}
                scope="company"
                own={a.paths || {}}
                canEdit={canEdit}
                saving={saving}
                keyPrefix={keyPrefix}
                onPick={(slot, level, start) => setPicking({ account: a, dest: null, slot, level, start })}
                onClear={(slot, level) => save(a, null, slot, level, null)}
              />
            </div>
          );
        }
        const list = a.destinations && a.destinations.length ? a.destinations : [null];
        return (
          <div key={keyPrefix} className="integration-section" data-testid={keyPrefix}>
            {head}
            {list.map((d) => (
              <div key={d ? d.destination : "none"} className="folder-paths-destination">
                {d && list.length > 1 ? <div className="mb-1"><b>{d.name}</b></div> : null}
                {!d ? <small className="text-muted d-block mb-1">No folders of this project&apos;s own on this account yet - what is shown comes from the company defaults.</small> : null}
                <SlotsBlock
                  slots={slots}
                  scope="project"
                  own={(d && d.own) || {}}
                  effective={d ? d.effective : a.effective}
                  canEdit={canEdit}
                  saving={saving}
                  keyPrefix={d && list.length > 1 ? `${keyPrefix}-${d.destination}` : keyPrefix}
                  onPick={(slot, level, start) => setPicking({ account: a, dest: d, slot, level, start })}
                  onClear={(slot, level) => save(a, d, slot, level, null)}
                />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export function FolderPathsModal({ projectId, projectTitle, onClose }) {
  return (
    <Modal open title={`Procore / ACC folders${projectTitle ? ` - ${projectTitle}` : ""}`} onCancel={onClose} footer={null} width={1040} className="integration-settings-modal" destroyOnClose>
      <FolderPathsPanel scope="project" projectId={projectId} />
    </Modal>
  );
}

/** Open the project's Procore / ACC folders from anywhere ("No folder is set - set it"). */
export function openFolderPaths(projectId, projectTitle) {
  const el = document.createElement("div");
  document.body.appendChild(el);
  const close = () => {
    ReactDOM.unmountComponentAtNode(el);
    el.remove();
  };
  ReactDOM.render(<FolderPathsModal projectId={projectId} projectTitle={projectTitle} onClose={close} />, el);
}

/** "No ... folder is set": the message, with a button that opens the folders. */
export function offerFolderPaths(projectId, message, projectTitle) {
  Modal.confirm({
    title: "No Procore / ACC folder set",
    content: message,
    okText: "Set folders",
    cancelText: "Not now",
    onOk: () => openFolderPaths(projectId, projectTitle),
  });
}
